import apiClient from "./api";
import {
  KbDocumentDto,
  DocumentStatusDto,
  KbIngestionRunDto,
  GenerateUploadUrlRequest,
  UploadUrlResponse,
  ConfirmUploadRequest,
  KbStatsDto,
  PaginatedResult,
  GetDocumentsParams,
  KbTestRetrievalRequest,
  KbTestRetrievalResponse,
  KbWebSourceDto,
  KbWebSourcePageDto,
  WebSourcePagesResponse,
  CreateWebSourceRequest,
  UpdateWebSourceRequest,
  KbFolderDto,
  KbFolderTreeDto,
  CreateFolderRequest,
  RenameFolderRequest,
  MoveItemRequest,
} from "../types/knowledgeBase";

export const knowledgeBaseService = {
  /**
   * Generate a presigned URL for uploading a document to S3
   */
  getUploadUrl: async (
    request: GenerateUploadUrlRequest,
  ): Promise<UploadUrlResponse> => {
    const response = await apiClient.post("/kb/upload-url", request);
    return response.data;
  },

  /**
   * Confirm upload after file is uploaded to S3
   * Creates the document record, metadata sidecar, and triggers Bedrock ingestion
   */
  confirmUpload: async (
    request: ConfirmUploadRequest,
  ): Promise<DocumentStatusDto> => {
    const response = await apiClient.post("/kb/documents/confirm", request);
    return response.data;
  },

  /**
   * Upload a file directly to S3 using the presigned URL
   * @param presignedUrl The presigned S3 URL
   * @param file The file to upload
   * @param contentType The content type (must match what was used to generate the presigned URL)
   * @param onProgress Optional progress callback
   */
  uploadFileToS3: async (
    presignedUrl: string,
    file: File,
    contentType: string,
    onProgress?: (percent: number) => void,
  ): Promise<void> => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener("progress", (event) => {
        if (event.lengthComputable && onProgress) {
          const percent = Math.round((event.loaded / event.total) * 100);
          onProgress(percent);
        }
      });

      xhr.addEventListener("load", () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      });

      xhr.addEventListener("error", () => {
        reject(new Error("Upload failed"));
      });

      xhr.open("PUT", presignedUrl);
      xhr.setRequestHeader("Content-Type", contentType);
      xhr.send(file);
    });
  },

  /**
   * Get all documents for the current tenant with search, filtering, and paging
   */
  getDocuments: async (
    params: GetDocumentsParams = {},
  ): Promise<PaginatedResult<KbDocumentDto>> => {
    const queryParams: Record<string, string | number> = {};
    if (params.search) queryParams.search = params.search;
    if (params.status) queryParams.status = params.status;
    if (params.page) queryParams.page = params.page;
    if (params.pageSize) queryParams.pageSize = params.pageSize;
    if (params.folderId !== undefined && params.folderId !== null)
      queryParams.folderId = params.folderId;

    const response = await apiClient.get("/kb/documents", {
      params: queryParams,
    });
    return response.data;
  },

  /**
   * Set the Global flag on a document
   */
  setDocumentGlobal: async (
    docId: string,
    isGlobal: boolean,
  ): Promise<KbDocumentDto> => {
    const response = await apiClient.patch(`/kb/documents/${docId}/global`, {
      isGlobal,
    });
    return response.data;
  },

  /**
   * Delete a document (soft delete)
   */
  deleteDocument: async (docId: string): Promise<void> => {
    await apiClient.delete(`/kb/documents/${docId}`);
  },

  bulkDeleteDocuments: async (ids: string[]): Promise<void> => {
    await apiClient.post("/kb/documents/bulk-delete", { documentIds: ids });
  },

  bulkMoveDocuments: async (
    ids: string[],
    folderId: string | null,
  ): Promise<void> => {
    await apiClient.post("/kb/documents/bulk-move", {
      documentIds: ids,
      folderId,
    });
  },

  bulkSetGlobalDocuments: async (
    ids: string[],
    isGlobal: boolean,
  ): Promise<void> => {
    await apiClient.post("/kb/documents/bulk-global", {
      documentIds: ids,
      isGlobal,
    });
  },

  bulkDeleteWebSources: async (ids: string[]): Promise<void> => {
    await apiClient.post("/kb/web-sources/bulk-delete", { webSourceIds: ids });
  },

  bulkMoveWebSources: async (
    ids: string[],
    folderId: string | null,
  ): Promise<void> => {
    await apiClient.post("/kb/web-sources/bulk-move", {
      webSourceIds: ids,
      folderId,
    });
  },

  bulkSetGlobalWebSources: async (
    ids: string[],
    isGlobal: boolean,
  ): Promise<void> => {
    await apiClient.post("/kb/web-sources/bulk-global", {
      webSourceIds: ids,
      isGlobal,
    });
  },

  /**
   * Get the current status of a document
   */
  getDocumentStatus: async (docId: string): Promise<DocumentStatusDto> => {
    const response = await apiClient.get(`/kb/documents/${docId}/status`);
    return response.data;
  },

  /**
   * Trigger a manual ingestion run
   */
  startManualIngestion: async (): Promise<KbIngestionRunDto> => {
    const response = await apiClient.post("/kb/resync");
    return response.data;
  },

  /**
   * Get all ingestion runs for the current tenant
   */
  getIngestionRuns: async (): Promise<KbIngestionRunDto[]> => {
    const response = await apiClient.get("/kb/ingestion-runs");
    return response.data;
  },

  /**
   * Get Knowledge Base statistics
   */
  getStats: async (): Promise<KbStatsDto> => {
    const response = await apiClient.get("/kb/stats");
    return response.data;
  },

  /**
   * Test Knowledge Base retrieval with a query
   * Returns matching chunks with their relevance scores
   */
  testRetrieval: async (
    request: KbTestRetrievalRequest,
  ): Promise<KbTestRetrievalResponse> => {
    const response = await apiClient.post("/kb/test-retrieval", request);
    return response.data;
  },

  // ============ Web Source Methods ============

  /**
   * Get all web sources for the current tenant, optionally filtered by folder
   */
  getWebSources: async (
    folderId?: string | null,
  ): Promise<KbWebSourceDto[]> => {
    const params: Record<string, string> = {};
    if (folderId) params.folderId = folderId;
    const response = await apiClient.get("/kb/web-sources", { params });
    return response.data;
  },

  /**
   * Get a specific web source by ID
   */
  getWebSource: async (id: string): Promise<KbWebSourceDto> => {
    const response = await apiClient.get(`/kb/web-sources/${id}`);
    return response.data;
  },

  /**
   * Create a new web crawler source
   */
  createWebSource: async (
    request: CreateWebSourceRequest,
  ): Promise<KbWebSourceDto> => {
    const response = await apiClient.post("/kb/web-sources", request);
    return response.data;
  },

  /**
   * Update a web source configuration
   */
  updateWebSource: async (
    id: string,
    request: UpdateWebSourceRequest,
  ): Promise<KbWebSourceDto> => {
    const response = await apiClient.put(`/kb/web-sources/${id}`, request);
    return response.data;
  },

  /**
   * Delete a web source
   */
  deleteWebSource: async (id: string): Promise<void> => {
    await apiClient.delete(`/kb/web-sources/${id}`);
  },

  /**
   * Set the Global flag on a web source
   */
  setWebSourceGlobal: async (
    id: string,
    isGlobal: boolean,
  ): Promise<KbWebSourceDto> => {
    const response = await apiClient.patch(`/kb/web-sources/${id}/global`, {
      isGlobal,
    });
    return response.data;
  },

  /**
   * Trigger a re-crawl for a web source
   */
  syncWebSource: async (id: string): Promise<KbWebSourceDto> => {
    const response = await apiClient.post(`/kb/web-sources/${id}/sync`);
    return response.data;
  },

  /**
   * Lists every URL from the latest crawl of a web source, including the ones that were
   * skipped or failed, with the reason.
   */
  getWebSourcePages: async (
    webSourceId: string,
  ): Promise<WebSourcePagesResponse> => {
    const response = await apiClient.get(
      `/kb/web-sources/${webSourceId}/pages`,
    );
    return response.data;
  },

  /** Queues a re-crawl of a single page to refresh its content. */
  syncWebSourcePage: async (
    webSourceId: string,
    pageId: string,
  ): Promise<KbWebSourcePageDto> => {
    const response = await apiClient.post(
      `/kb/web-sources/${webSourceId}/pages/${pageId}/sync`,
    );
    return response.data;
  },

  /**
   * Excludes pages from the knowledge base (deletes their content and keeps them out of
   * future crawls), or includes them again (queues a re-crawl of each).
   */
  setWebSourcePagesExcluded: async (
    webSourceId: string,
    pageIds: string[],
    excluded: boolean,
  ): Promise<KbWebSourcePageDto[]> => {
    const response = await apiClient.post(
      `/kb/web-sources/${webSourceId}/pages/exclusion`,
      { pageIds, excluded },
    );
    return response.data;
  },

  // ============ Folder Methods ============

  getFolderTree: async (): Promise<KbFolderTreeDto[]> => {
    const response = await apiClient.get("/kb/folders/tree");
    return response.data;
  },

  createFolder: async (request: CreateFolderRequest): Promise<KbFolderDto> => {
    const response = await apiClient.post("/kb/folders", request);
    return response.data;
  },

  renameFolder: async (
    id: string,
    request: RenameFolderRequest,
  ): Promise<KbFolderDto> => {
    const response = await apiClient.patch(`/kb/folders/${id}`, request);
    return response.data;
  },

  deleteFolder: async (id: string): Promise<void> => {
    await apiClient.delete(`/kb/folders/${id}`);
  },

  moveDocument: async (
    docId: string,
    request: MoveItemRequest,
  ): Promise<KbDocumentDto> => {
    const response = await apiClient.patch(
      `/kb/documents/${docId}/folder`,
      request,
    );
    return response.data;
  },

  moveWebSource: async (
    wsId: string,
    request: MoveItemRequest,
  ): Promise<KbWebSourceDto> => {
    const response = await apiClient.patch(
      `/kb/web-sources/${wsId}/folder`,
      request,
    );
    return response.data;
  },

  setFolderGlobal: async (
    id: string,
    isGlobal: boolean,
  ): Promise<KbFolderDto> => {
    const response = await apiClient.patch(`/kb/folders/${id}/global`, {
      isGlobal,
    });
    return response.data;
  },
};
